/**
 * @fileoverview The shape of the slot a photograph fills in a share picture, which
 * is the shape of its crop box (B86): the whole frame for most layouts, the photo
 * area of the card, a half of Before & after, a tile of the collage. One home for
 * the arithmetic, used by the drawing (frameLayouts, buildFrames) and by the crop
 * screen, so the box and the picture never disagree. Pure, so it is tested without
 * a page (slotShape.b86.test.mjs).
 *
 * Where: the frames' drawing (features/share/frames) and the share studio's Move &
 * zoom (features/share/studio).
 * Settings: none (the sizes and SHARE_TALL_RATIO are read by the callers).
 *
 * @module slotShape
 */

/** A rectangle's width and height, in pixels. */
export interface SlotSize {
  /** Width. */
  w: number;
  /** Height. */
  h: number;
}

/**
 * A slot's shape as its width over its height (1 for one with no size).
 *
 * @param slot - The slot's size.
 * @returns The width over the height.
 */
export function aspectOfRect(slot: SlotSize): number {
  return slot.w > 0 && slot.h > 0 ? slot.w / slot.h : 1;
}

/** The card's widths: the card, its margin, the room for lettering inside it, and the gaps around the lettering. */
export interface CardBox {
  /** The card's width. */
  cardW: number;
  /** Its margin around the text. */
  pad: number;
  /** The lettering's width. */
  inner: number;
  /** The gap above the lettering. */
  textPad: number;
  /** The gap under it. */
  foot: number;
}

/**
 * The card's widths for a frame (they do not depend on the lettering).
 *
 * @param W - The frame's width.
 * @param tall - The frame is a tall one (a story or a pin), whose card is wider.
 * @returns The card's widths.
 */
export function cardBox(W: number, tall: boolean): CardBox {
  const cardW = Math.round(W * (tall ? 0.82 : 0.78));
  return { cardW, pad: Math.round(cardW * 0.07), inner: cardW - Math.round(cardW * 0.07) * 2, textPad: Math.round(cardW * 0.06), foot: Math.round(cardW * 0.07) };
}

/**
 * The card layout's photo and card heights. Large lettering takes its room from
 * the photograph rather than running the card off the frame; the photograph keeps
 * at least a third of the card.
 *
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param tall - The frame is a tall one.
 * @param textHeight - The lettering's height under the photograph.
 * @returns The card's widths with the photo area's height (photoH) and the card's (cardH).
 */
export function cardSlot(W: number, H: number, tall: boolean, textHeight: number): CardBox & { photoH: number; cardH: number } {
  const box = cardBox(W, tall);
  const room = Math.round(H * 0.9) - box.textPad - textHeight - box.foot;
  const photoH = Math.max(Math.round(box.cardW * 0.34), Math.min(Math.round(box.cardW * 3 / 4), room));
  return { ...box, photoH, cardH: photoH + box.textPad + textHeight + box.foot };
}

/**
 * One half of Before & after: stacked on a tall frame, side by side otherwise.
 *
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param down - The two photographs stack (a tall frame).
 * @returns The half's size.
 */
export function halfSlot(W: number, H: number, down: boolean): SlotSize {
  return down ? { w: W, h: Math.floor(H / 2) } : { w: Math.floor(W / 2), h: H };
}
